import type { ActivityAction, ActivityEvent } from '@fleequid-secrets/shared';

// An event as a sentence: who, a verb, what it was about (a key, a section, a person), and a detail in plain words.

export type ActivityKind = 'changes' | 'reads' | 'access';

export interface Sentence {
    who: string;
    verb: string;
    // A key reads as code; a section, an environment or a person as words.
    subject: { text: string; code: boolean } | null;
    extra: string | null;
    kind: ActivityKind;
    failed: boolean;
}

const READS: ReadonlySet<ActivityAction> = new Set([
    'variable.revealed',
    'variable.copied',
    'version.revealed',
    'section.revealed',
    'section.exported',
]);
const ACCESS: ReadonlySet<ActivityAction> = new Set([
    'session.signed_in',
    'session.sign_in_failed',
    'password.changed',
    'user.created',
    'user.updated',
    'user.password_reset',
    'user.removed',
]);

export const kindOf = (action: ActivityAction): ActivityKind => (READS.has(action) ? 'reads' : ACCESS.has(action) ? 'access' : 'changes');

const FIELD_WORDS: Readonly<Record<string, string>> = {
    name: 'name',
    slug: 'address',
    color: 'colour',
    protected: 'protection',
    description: 'description',
};

const num = (value: unknown): number => (typeof value === 'number' ? value : 0);
const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const list = (value: unknown): string[] => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);
const count = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;
const fields = (value: unknown): string =>
    list(value)
        .map((field) => FIELD_WORDS[field] ?? field)
        .join(', ');

export function sentence(event: ActivityEvent): Sentence {
    const { detail } = event;
    const key = event.variable ? { text: event.variable.key, code: true } : null;
    const section = event.section ? { text: event.section.name, code: false } : null;
    const environment = event.environment ? { text: event.environment.name, code: false } : null;
    const email = text(detail.email) ? { text: text(detail.email), code: false } : null;
    const base = { who: event.actor?.name ?? 'Fleequid Secrets', kind: kindOf(event.action), failed: false, extra: null };

    switch (event.action) {
        case 'session.signed_in':
            return { ...base, verb: 'signed in', subject: null };
        case 'session.sign_in_failed':
            return { ...base, who: 'Someone', verb: 'failed to sign in as', subject: email, failed: true };
        case 'password.changed':
            return { ...base, verb: 'changed their password', subject: null };
        case 'environment.created':
            return { ...base, verb: 'created the environment', subject: environment };
        case 'environment.updated':
            return { ...base, verb: 'changed', subject: environment, extra: fields(detail.fields) || null };
        case 'environment.deleted':
            return {
                ...base,
                verb: 'deleted the environment',
                subject: environment,
                extra: `${count(num(detail.sections), 'section')}, ${count(num(detail.variables), 'variable')}`,
            };
        case 'section.created':
            return { ...base, verb: 'created the section', subject: section };
        case 'section.updated':
            return { ...base, verb: 'changed the section', subject: section, extra: fields(detail.fields) || null };
        case 'section.deleted':
            return { ...base, verb: 'deleted the section', subject: section, extra: count(num(detail.variables), 'variable') };
        case 'section.imported': {
            const parts = [
                num(detail.added) && `${num(detail.added)} new`,
                num(detail.changed) && `${num(detail.changed)} changed`,
                num(detail.removed) && `${num(detail.removed)} removed`,
            ].filter(Boolean);

            return { ...base, verb: 'imported a .env into', subject: section, extra: parts.join(', ') || null };
        }
        case 'section.exported':
            return { ...base, verb: 'exported', subject: section, extra: `${count(num(detail.count), 'variable')} as .env` };
        case 'section.revealed':
            return {
                ...base,
                verb: detail.purpose === 'copy' ? 'copied the values of' : 'revealed the values of',
                subject: section,
                extra: count(num(detail.count), 'variable'),
            };
        case 'section.copied':
            return {
                ...base,
                verb: 'copied keys into',
                subject: section,
                extra: `${count(num(detail.added), 'key')} from ${text(detail.from)}${detail.values ? ', with values' : ''}`,
            };
        case 'variable.created':
            return { ...base, verb: 'added', subject: key };
        case 'variable.updated': {
            const changed = list(detail.fields);
            const extra = [
                changed.includes('value') ? `value, now version ${num(detail.version)}` : null,
                changed.includes('key') ? `renamed from ${text(detail.previousKey)}` : null,
                changed.includes('note') ? 'note' : null,
            ].filter(Boolean);

            return { ...base, verb: 'changed', subject: key, extra: extra.join(' · ') || null };
        }
        case 'variable.deleted':
            return { ...base, verb: 'deleted', subject: key };
        case 'variable.restored':
            return { ...base, verb: 'brought back', subject: key };
        case 'variable.revealed':
            return { ...base, verb: 'revealed', subject: key };
        case 'variable.copied':
            return { ...base, verb: 'copied', subject: key };
        case 'version.revealed':
            return { ...base, verb: `revealed version ${num(detail.version)} of`, subject: key };
        case 'version.restored':
            return { ...base, verb: `restored version ${num(detail.from)} of`, subject: key, extra: `as version ${num(detail.version)}` };
        case 'user.created':
            return { ...base, verb: 'added', subject: email, extra: text(detail.role) === 'admin' ? 'as an admin' : 'as a member' };
        case 'user.updated':
            return {
                ...base,
                verb: 'changed',
                subject: email,
                extra: detail.role ? `now ${text(detail.role) === 'admin' ? 'an admin' : 'a member'}` : null,
            };
        case 'user.password_reset':
            return { ...base, verb: 'reset the password of', subject: email };
        case 'user.removed':
            return { ...base, verb: 'removed', subject: email };
    }
}
